import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useWorkspace, useCards, useDecks, useUndo, useStorage } from '../hooks/useApi';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, Search, MoreVertical, Edit, Trash2, 
  ChevronDown, ArrowUpAZ, ArrowDownAZ, Layers, FolderPlus, 
  Undo2, FileDown, FileUp, Copy, Check, X, Cloud, CloudUpload, CloudDownload, Moon, Sun, Upload, FileText, FileSpreadsheet, Library, AlertCircle, Loader2
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  initGoogleDrive,
  hasGoogleAccess,
  backupToGoogleDrive,
  restoreFromGoogleDrive,
  checkGoogleDriveBackup,
  startGoogleRedirectAuth,
  handleGoogleRedirectCallback
} from '../services/cloudBackup';
import { saveOrShareFile } from '../lib/fileUtils';

// Card Editor Component
function CardEditor({ isOpen, onClose, card, onSave, workspaceId }) {
  const storage = useStorage();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [fieldIdCounter, setFieldIdCounter] = useState(0);

  useEffect(() => {
    if (card) {
      setTitle(card.title || '');
      setBody(card.body || '');
      const fieldList = Object.entries(card.fields || {}).map(([key, val], idx) => ({
        id: idx,
        key,
        value: typeof val === 'object' ? val.value : val
      }));
      setFields(fieldList.length > 0 ? fieldList : [{ id: 0, key: '', value: '' }]);
      setFieldIdCounter(fieldList.length || 1);
    } else {
      setTitle('');
      setBody('');
      setFields([{ id: 0, key: '', value: '' }]);
      setFieldIdCounter(1);
    }
  }, [card, isOpen]);

  const addField = () => {
    setFields([...fields, { id: fieldIdCounter, key: '', value: '' }]);
    setFieldIdCounter(prev => prev + 1);
  };
  
  const removeField = (id) => {
    const newFields = fields.filter(f => f.id !== id);
    setFields(newFields.length > 0 ? newFields : [{ id: fieldIdCounter, key: '', value: '' }]);
    if (newFields.length === 0) setFieldIdCounter(prev => prev + 1);
  };

  const updateField = (id, key, value) => {
    setFields(fields.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    // Combine duplicate field names into comma-separated values
    const fieldObj = {};
    fields.forEach(f => {
      const key = f.key.trim();
      const value = f.value.trim();
      if (key && value) {
        if (fieldObj[key]) {
          // Append to existing value with comma
          fieldObj[key] = fieldObj[key] + ', ' + value;
        } else {
          fieldObj[key] = value;
        }
      }
    });

    // Validate: card must have notes OR at least one field
    const hasBody = body.trim().length > 0;
    const hasFields = Object.keys(fieldObj).length > 0;
    
    if (!hasBody && !hasFields) {
      toast.error('Card must have at least one field or a note to be saved');
      return;
    }

    setSaving(true);
    try {
      const data = { title: title.trim(), body: body.trim(), fields: fieldObj };

      if (card?.id) {
        await storage.updateCard(card.id, data);
      } else {
        await storage.createCard(data);
      }

      onSave();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{card ? 'Edit Card' : 'New Card'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Card title" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Optional notes..." rows={3} />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Fields</Label>
              <Button variant="outline" size="sm" onClick={addField}>
                <Plus className="h-3 w-3 mr-1" /> Add Field
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field) => (
                <div key={field.id} className="flex gap-2 items-center">
                  <Input 
                    value={field.key} 
                    onChange={(e) => updateField(field.id, 'key', e.target.value)}
                    placeholder="Field name"
                    className="flex-1"
                  />
                  <span className="text-muted-foreground">:</span>
                  <Input 
                    value={field.value} 
                    onChange={(e) => updateField(field.id, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeField(field.id)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Card'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Quick Entry Bar Component
function QuickEntryBar({ cards, onSuccess }) {
  const storage = useStorage();
  const [cardInput, setCardInput] = useState('');
  const [fieldInput, setFieldInput] = useState('');
  const [valueInput, setValueInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const cardInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (cardInput.trim().length > 0) {
      const query = cardInput.toLowerCase();
      const matches = cards
        .filter(c => c.title.toLowerCase().includes(query))
        .slice(0, 5);
      setSuggestions(matches);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [cardInput, cards]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          cardInputRef.current && !cardInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCardKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (card) => {
    setCardInput(card.title);
    setShowSuggestions(false);
    // Focus field input
    document.querySelector('[data-quick-field]')?.focus();
  };

  const isNewCard = () => {
    if (!cardInput.trim()) return false;
    return !cards.some(c => c.title.toLowerCase() === cardInput.trim().toLowerCase());
  };

  const handleSubmit = async () => {
    const cardTitle = cardInput.trim();
    const fieldName = fieldInput.trim();
    const fieldValue = valueInput.trim();

    if (!cardTitle) {
      toast.error('Card title is required');
      return;
    }

    if (!fieldName || !fieldValue) {
      toast.error('Both field and value are required');
      return;
    }

    setSubmitting(true);
    try {
      // Find existing card or create new one
      const existingCard = cards.find(c => c.title.toLowerCase() === cardTitle.toLowerCase());
      
      if (existingCard) {
        // Add field to existing card
        const updatedFields = { ...existingCard.fields };
        updatedFields[fieldName] = fieldValue;
        await storage.updateCard(existingCard.id, { fields: updatedFields });
        toast.success(`Added "${fieldName}: ${fieldValue}" to "${existingCard.title}"`);
      } else {
        // Create new card with field
        await storage.createCard({
          title: cardTitle,
          body: '',
          fields: { [fieldName]: fieldValue }
        });
        toast.success(`Created "${cardTitle}" with field "${fieldName}: ${fieldValue}"`);
      }

      // Clear inputs
      setCardInput('');
      setFieldInput('');
      setValueInput('');
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="bg-card border rounded-lg p-3 shadow-sm" data-testid="quick-entry-bar">
      <div className="flex items-center gap-2">
        {/* Card Input with Autocomplete */}
        <div className="relative flex-1 min-w-0">
          <Input
            ref={cardInputRef}
            value={cardInput}
            onChange={(e) => setCardInput(e.target.value)}
            onKeyDown={handleCardKeyDown}
            onFocus={() => cardInput.trim() && setSuggestions(cards.filter(c => c.title.toLowerCase().includes(cardInput.toLowerCase())).slice(0, 5))}
            placeholder="Card title..."
            className="pr-16"
            data-testid="quick-card-input"
          />
          {cardInput.trim() && (
            <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-0.5 rounded ${
              isNewCard() 
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            }`}>
              {isNewCard() ? 'new' : 'exists'}
            </span>
          )}
          
          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div 
              ref={suggestionsRef}
              className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 overflow-hidden"
            >
              {suggestions.map((card, idx) => (
                <button
                  key={card.id}
                  onClick={() => selectSuggestion(card)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                    idx === selectedIndex ? 'bg-muted' : ''
                  }`}
                >
                  {card.title}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="text-muted-foreground hidden sm:inline">:</span>

        {/* Field Input */}
        <Input
          value={fieldInput}
          onChange={(e) => setFieldInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Field"
          className="flex-1 min-w-0"
          data-quick-field
          data-testid="quick-field-input"
        />

        <span className="text-muted-foreground hidden sm:inline">=</span>

        {/* Value Input */}
        <Input
          value={valueInput}
          onChange={(e) => setValueInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Value"
          className="flex-1 min-w-0"
          data-testid="quick-value-input"
        />

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit} 
          disabled={submitting || !cardInput.trim() || !fieldInput.trim() || !valueInput.trim()}
          size="icon"
          data-testid="quick-submit-btn"
        >
          {submitting ? (
            <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent rounded-full" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

// Deck Creator with Seed Card Selection
function DeckCreator({ isOpen, onClose, onSave, workspaceId, initialCards = [] }) {
  const storage = useStorage();
  const { cards } = useCards();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCards(new Set(initialCards || []));
      setSearchQuery('');
      setName('');
      setDescription('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredCards = useMemo(() => {
    if (!searchQuery.trim()) return cards;
    const q = searchQuery.toLowerCase();
    return cards.filter(c => c.title.toLowerCase().includes(q));
  }, [cards, searchQuery]);

  const toggleCard = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Deck name is required');
      return;
    }

    setCreating(true);
    try {
      const result = await storage.createDeck(name.trim(), description.trim(), Array.from(selectedCards));
      toast.success(`Deck created with ${result.total_cards_after_closure} cards (${result.auto_included} auto-included for closure)`);
      onSave();
      onClose();
      setName('');
      setDescription('');
      setSelectedCards(new Set());
    } catch (error) {
      toast.error(error.message || 'Failed to create deck');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Deck</DialogTitle>
          <DialogDescription>
            Select seed cards and the system will auto-expand to include all related cards for a closed graph.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Deck Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Deck" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
          </div>
          <div>
            <Label className="flex items-center justify-between">
              <span>Select Seed Cards ({selectedCards.size} selected)</span>
            </Label>
            <Input 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cards..."
              className="mt-2"
            />
            <ScrollArea className="h-64 mt-2 border rounded-md p-2">
              {filteredCards.map(card => (
                <div key={card.id} className="flex items-center gap-2 py-2 px-2 hover:bg-muted/50 rounded">
                  <Checkbox 
                    checked={selectedCards.has(card.id)}
                    onCheckedChange={() => toggleCard(card.id)}
                  />
                  <span className="flex-1">{card.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {Object.keys(card.fields || {}).length} fields
                  </span>
                </div>
              ))}
              {filteredCards.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No cards found</p>
              )}
            </ScrollArea>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : `Create Deck (${selectedCards.size} seed cards)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add to Deck Dialog
function AddToDeckDialog({ isOpen, onClose, card, workspaceId, onSuccess }) {
  const storage = useStorage();
  const { decks, refetch: refetchDecks } = useDecks();
  const [selectedDecks, setSelectedDecks] = useState(new Set());
  const [showNewDeck, setShowNewDeck] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isOpen && card) {
      setSelectedDecks(new Set(card.decks || []));
    }
  }, [isOpen, card]);

  const toggleDeck = (deckId) => {
    const newSelected = new Set(selectedDecks);
    if (newSelected.has(deckId)) {
      newSelected.delete(deckId);
    } else {
      newSelected.add(deckId);
    }
    setSelectedDecks(newSelected);
  };

  const handleSave = async () => {
    if (!card) return;
    setAdding(true);

    try {
      for (const deckId of selectedDecks) {
        if (!card.decks?.includes(deckId)) {
          await storage.addCardsToDeck(deckId, [card.id]);
        }
      }

      toast.success('Card added to decks');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to add to decks');
    } finally {
      setAdding(false);
    }
  };

  if (showNewDeck) {
    return (
      <DeckCreator
        isOpen={true}
        onClose={() => setShowNewDeck(false)}
        onSave={() => {
          refetchDecks();
          setShowNewDeck(false);
        }}
        workspaceId={workspaceId}
        initialCards={card ? [card.id] : []}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to Deck</DialogTitle>
          <DialogDescription>Select decks to add "{card?.title}" to</DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-64 mt-4">
          {decks.map(deck => (
            <div key={deck.id} className="flex items-center gap-2 py-2 px-2 hover:bg-muted/50 rounded">
              <Checkbox 
                checked={selectedDecks.has(deck.id)}
                onCheckedChange={() => toggleDeck(deck.id)}
              />
              <span className="flex-1">{deck.name}</span>
              <span className="text-xs text-muted-foreground">
                {deck.cards?.length || 0} cards
              </span>
            </div>
          ))}
          {decks.length === 0 && (
            <p className="text-center text-muted-foreground py-4">No decks yet</p>
          )}
        </ScrollArea>
        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={() => setShowNewDeck(true)}>
            <FolderPlus className="h-4 w-4 mr-2" /> New Deck
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={adding}>
              {adding ? 'Adding...' : 'Add to Decks'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Workspace Selector

// Native <dialog> based modal — renders in the browser top layer, immune to
// all z-index and stacking context issues from Radix or sticky headers.
// WsModal using AlertDialog for better click handling
function WsModal({ open, onClose, children }) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent className="max-w-md">
        {children}
      </AlertDialogContent>
    </AlertDialog>
  );
}

// WorkspaceSelector is purely a dropdown — no dialogs live here.
// All dialog state is lifted to FieldMode to prevent Radix focus-restoration
// from closing dialogs that were opened from inside the dropdown.
function WorkspaceSelector({ onNew, onRename, onDelete, onDuplicate }) {
  const { currentWorkspace, workspaces, selectWorkspace } = useWorkspace();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Layers className="h-4 w-4" />
          {currentWorkspace?.name || 'Select Workspace'}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.length === 0 ? (
          <DropdownMenuItem disabled>
            <span className="text-muted-foreground">Loading...</span>
          </DropdownMenuItem>
        ) : (
          workspaces.map(ws => (
            <DropdownMenuItem
              key={ws.id}
              className="flex justify-between items-center group"
              onClick={() => selectWorkspace(ws)}
            >
              <span className="flex items-center gap-2 flex-1">
                {ws.id === currentWorkspace?.id && <Check className="h-4 w-4" />}
                {ws.name}
                {ws.is_main && <Badge variant="secondary" className="text-xs">Main</Badge>}
              </span>
              <span className="flex gap-1 opacity-0 group-hover:opacity-100">
                <button
                  className="p-1 hover:bg-muted rounded"
                  onClick={(e) => { e.stopPropagation(); onRename(ws); }}
                  title="Rename"
                >
                  <Edit className="h-3 w-3" />
                </button>
                <button
                  className="p-1 hover:bg-muted rounded"
                  onClick={(e) => { e.stopPropagation(); onDuplicate(ws); }}
                  title="Duplicate"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  className="p-1 hover:bg-destructive/20 rounded text-destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(ws); }}
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onNew}>
          <Plus className="h-4 w-4 mr-2" /> New Workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Main FieldMode Component
export function FieldMode() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const storage = useStorage();
  const { theme, toggleTheme } = useTheme();
  const { currentWorkspace, loading: wsLoading, selectWorkspace, refetch: refetchWorkspaces } = useWorkspace();
  const { cards, loading, refetch } = useCards({ deck: searchParams.get('deck') });
  const { decks, refetch: refetchDecks } = useDecks();
  const { lastDeleted, performUndo, checkUndo } = useUndo();

  // Workspace dialog state — lifted out of WorkspaceSelector to avoid
  // Radix dropdown focus-restoration closing dialogs immediately
  const [wsShowCreate, setWsShowCreate] = useState(false);
  const [wsNewName, setWsNewName] = useState('');
  const [wsCreating, setWsCreating] = useState(false);
  const [wsToDelete, setWsToDelete] = useState(null);
  const [wsToRename, setWsToRename] = useState(null);
  const [wsRenameValue, setWsRenameValue] = useState('');

  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('az');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteCard, setDeleteCard] = useState(null);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeckCreator, setShowDeckCreator] = useState(false);
  const [addToDeckCard, setAddToDeckCard] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importTab, setImportTab] = useState('file');
  const [importFile, setImportFile] = useState(null);
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  
  // Export state
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState('json');
  const [exporting, setExporting] = useState(false);
  const [exportingTo, setExportingTo] = useState(null);

  // Google Drive state
  const [googleReady, setGoogleReady] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleBackupInfo, setGoogleBackupInfo] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Bulk delete state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [bulkDeletePreview, setBulkDeletePreview] = useState(null);
  const [loadingBulkPreview, setLoadingBulkPreview] = useState(false);

  const currentDeck = searchParams.get('deck');
  const currentDeckInfo = decks.find(d => d.id === currentDeck);

  // Filter and sort cards
  const displayCards = useMemo(() => {
    let filtered = cards;
    
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = cards.filter(c => 
        c.title.toLowerCase().includes(q) ||
        Object.keys(c.fields || {}).some(k => k.toLowerCase().includes(q)) ||
        Object.values(c.fields || {}).some(v => {
          const val = typeof v === 'object' ? v.value : v;
          return val?.toLowerCase?.()?.includes(q);
        })
      );
    }

    return [...filtered].sort((a, b) => {
      const cmp = a.title.localeCompare(b.title);
      return sortOrder === 'az' ? cmp : -cmp;
    });
  }, [cards, search, sortOrder]);

  // Google Drive init
  useEffect(() => {
    async function initGDrive() {
      const redirectResult = handleGoogleRedirectCallback();
      if (redirectResult?.success) toast.success('Connected to Google Drive!');
      const ok = await initGoogleDrive();
      setGoogleReady(ok);
      setGoogleConnected(hasGoogleAccess());
      if (hasGoogleAccess()) {
        const info = await checkGoogleDriveBackup();
        setGoogleBackupInfo(info);
      }
    }
    initGDrive();
  }, []);

  // Delete card handlers
  const openDeleteDialog = async (card) => {
    setDeleteCard(card);
    setLoadingPreview(true);
    try {
      const preview = await storage.getCardDeletionPreview(card.id);
      setDeletionPreview(preview);
    } catch (error) {
      setDeletionPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Workspace handlers — live in FieldMode so dialogs are fully decoupled from dropdown

  const handleWsCreate = async () => {
    if (!wsNewName.trim()) return;
    setWsCreating(true);
    try {
      const ws = await storage.createWorkspace(wsNewName.trim());
      toast.success('Workspace created');
      refetchWorkspaces();
      selectWorkspace(ws);
      setWsShowCreate(false);
      setWsNewName('');
    } catch (error) {
      toast.error(error.message || 'Failed to create workspace');
    } finally {
      setWsCreating(false);
    }
  };

  const handleWsRename = async () => {
    if (!wsToRename || !wsRenameValue.trim()) return;
    try {
      await storage.updateWorkspace(wsToRename.id, { name: wsRenameValue.trim() });
      toast.success(`Workspace renamed to "${wsRenameValue.trim()}"`);
      setWsToRename(null);
      setWsRenameValue('');
      refetchWorkspaces();
    } catch (error) {
      toast.error(error.message || 'Failed to rename workspace');
    }
  };

  const handleWsDelete = async () => {
    if (!wsToDelete) return;
    try {
      await storage.deleteWorkspace(wsToDelete.id);
      toast.success(`Workspace "${wsToDelete.name}" deleted`);
      setWsToDelete(null);
      refetchWorkspaces();
    } catch (error) {
      toast.error(error.message || 'Failed to delete workspace');
    }
  };

  const handleWsDuplicate = async (ws) => {
    try {
      const result = await storage.duplicateWorkspace(ws.id);
      toast.success(`Workspace duplicated: ${result.cards_copied} cards copied`);
      refetchWorkspaces();
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate workspace');
    }
  };

  const openWsRename = (ws) => {
    setWsToRename(ws);
    setWsRenameValue(ws.name);
  };

  const handleDelete = async () => {
    if (!deleteCard) return;
    setIsDeleting(true);
    try {
      const result = await storage.deleteCard(deleteCard.id, true);
      const fieldsRemoved = result?.fields_removed || 0;
      toast.success(fieldsRemoved > 0 
        ? `Card deleted and ${fieldsRemoved} related field(s) removed`
        : 'Card deleted'
      );
      setDeleteCard(null);
      setDeletionPreview(null);
      refetch();
      checkUndo();
    } catch (error) {
      toast.error('Failed to delete card');
    } finally {
      setIsDeleting(false);
    }
  };

  // Undo handler
  const handleUndo = async () => {
    try {
      const result = await performUndo();
      toast.success(result.message);
      refetch();
      refetchDecks();
    } catch (error) {
      toast.error('Undo failed');
    }
  };

  // Bulk delete handlers
  const toggleBulkSelectMode = () => {
    if (bulkSelectMode) {
      // Exiting bulk mode - clear selections
      setSelectedCards(new Set());
      setBulkDeletePreview(null);
    }
    setBulkSelectMode(!bulkSelectMode);
  };

  const toggleCardSelection = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleBulkDeletePreview = async () => {
    if (selectedCards.size === 0) return;
    
    setLoadingBulkPreview(true);
    try {
      // Get preview for all selected cards
      const previews = [];
      const allAffectedCards = new Map(); // cardId -> card info
      
      for (const cardId of selectedCards) {
        const card = cards.find(c => c.id === cardId);
        if (card) {
          previews.push({ id: cardId, title: card.title, isSelected: true });
          
          // Get cascade preview
          const preview = await storage.getCardDeletionPreview(cardId);
          if (preview?.affected_cards) {
            for (const affected of preview.affected_cards) {
              if (!selectedCards.has(affected.card_id) && !allAffectedCards.has(affected.card_id)) {
                allAffectedCards.set(affected.card_id, affected);
              }
            }
          }
        }
      }
      
      setBulkDeletePreview({
        selected: previews,
        affected: Array.from(allAffectedCards.values()),
        totalSelected: selectedCards.size,
        totalAffected: allAffectedCards.size
      });
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setLoadingBulkPreview(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCards.size === 0) return;
    
    setIsDeleting(true);
    try {
      let deleted = 0;
      for (const cardId of selectedCards) {
        try {
          await storage.deleteCard(cardId, true);
          deleted++;
        } catch (cardErr) {
          // Card may have been auto-deleted as an orphan during a prior cascade — skip it
          if (cardErr.message !== 'Card not found') throw cardErr;
        }
      }
      
      toast.success(`Deleted ${deleted} cards`);
      setSelectedCards(new Set());
      setBulkDeletePreview(null);
      setBulkSelectMode(false);
      refetch();
      refetchDecks();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  // Import handlers
  // Parse CSV text
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"' && !inQuotes) inQuotes = true;
        else if (char === '"' && inQuotes) inQuotes = false;
        else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx]; });
        rows.push(row);
      }
    }
    return rows;
  };

  // Convert flat data to cards format (horizontal: each row = one card)
  const convertToCards = (rows) => {
    // Reserved keys: Title (card name), Notes (card notes only)
    const titleKeys = ['title', 'Title', 'name', 'Name', 'card', 'Card'];
    const notesKeys = ['notes', 'Notes'];
    const reservedKeys = [...titleKeys, ...notesKeys, 'id', 'ID'];
    
    return rows.map(row => {
      const title = row.title || row.Title || row.name || row.Name || row.card || row.Card;
      if (!title) return null;
      
      const notes = row.notes || row.Notes || '';
      const fields = {};
      
      for (const [key, value] of Object.entries(row)) {
        if (!reservedKeys.includes(key) && value && value.toString().trim()) {
          fields[key] = value.toString().trim();
        }
      }
      
      return { title: title.trim(), body: notes.trim(), fields };
    }).filter(Boolean);
  };

  // Convert vertical format spreadsheet (column A = labels, column B+ = card data)
  const convertVerticalToCards = (sheet) => {
    const titleLabels = ['title', 'name', 'card'];
    const notesLabels = ['notes'];
    const cards = [];
    
    // Get the range of the sheet
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    const numCols = range.e.c + 1;
    const numRows = range.e.r + 1;
    
    // Column A (index 0) contains labels, columns B+ contain card data
    for (let col = 1; col < numCols; col++) {
      let title = '';
      let body = '';
      const fields = {};
      
      for (let row = 0; row < numRows; row++) {
        const labelCell = sheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
        const valueCell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
        
        const label = labelCell?.v?.toString().trim() || '';
        const value = valueCell?.v?.toString().trim() || '';
        
        if (!label || !value) continue;
        
        const labelLower = label.toLowerCase();
        
        if (titleLabels.includes(labelLower)) {
          title = value;
        } else if (notesLabels.includes(labelLower)) {
          body = value;
        } else {
          // It's a field
          fields[label] = value;
        }
      }
      
      if (title) {
        cards.push({ title, body, fields });
      }
    }
    
    return cards;
  };

  // Detect if spreadsheet is vertical format (column A = labels)
  const isVerticalFormat = (sheet) => {
    const titleLabels = ['title', 'name', 'card'];
    
    // Check first few cells in column A
    const a1 = sheet['A1']?.v?.toString().toLowerCase().trim() || '';
    const a2 = sheet['A2']?.v?.toString().toLowerCase().trim() || '';
    
    // If A1 is a title label and A2 is notes or another label, it's vertical
    if (titleLabels.includes(a1)) {
      const notesLabels = ['notes'];
      // Check if A2 looks like another label (notes, or any non-empty string that's not a typical value)
      if (notesLabels.includes(a2) || (a2 && !a2.includes(' ') && a2.length < 30)) {
        // Additional check: B1 should have actual data (the card title value)
        const b1 = sheet['B1']?.v?.toString().trim() || '';
        if (b1) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Handle file selection for import
  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setImportFile(file);
    e.target.value = '';
  };

  // Get file icon
  const getImportFileIcon = (fileName) => {
    if (!fileName) return <FileUp className="h-8 w-8" />;
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.json')) return <FileText className="h-8 w-8 text-blue-500" />;
    if (lower.endsWith('.csv')) return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
    if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
    return <FileUp className="h-8 w-8" />;
  };

  const handleImportPreview = async () => {
    setImporting(true);
    try {
      let cards = [];
      let deckName = 'Imported Deck';

      if (importTab === 'file' && importFile) {
        const fileName = importFile.name.toLowerCase();
        
        if (fileName.endsWith('.json')) {
          const text = await importFile.text();
          const parsed = JSON.parse(text);
          cards = Array.isArray(parsed) ? parsed : parsed.cards || [parsed];
          deckName = parsed.deck_name || 'JSON Import';
        } else if (fileName.endsWith('.csv')) {
          const text = await importFile.text();
          const rows = parseCSV(text);
          cards = convertToCards(rows);
          deckName = importFile.name.replace('.csv', '');
        } else if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
          const buffer = await importFile.arrayBuffer();
          const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          
          // Auto-detect format: vertical (column A = labels) or horizontal (row 1 = headers)
          if (isVerticalFormat(sheet)) {
            cards = convertVerticalToCards(sheet);
            toast.info('Detected vertical format spreadsheet');
          } else {
            const rows = XLSX.utils.sheet_to_json(sheet);
            cards = convertToCards(rows);
          }
          deckName = importFile.name.replace(/\.xlsx?$/, '');
        } else {
          throw new Error('Unsupported file type');
        }
      } else if (importTab === 'text' && importData.trim()) {
        const parsed = JSON.parse(importData);
        cards = Array.isArray(parsed) ? parsed : parsed.cards || [parsed];
        deckName = parsed.deck_name || 'Imported Deck';
      } else {
        toast.error('Please select a file or enter JSON text');
        setImporting(false);
        return;
      }

      if (cards.length === 0) {
        toast.error('No valid cards found');
        setImporting(false);
        return;
      }

      const preview = await storage.previewImport(deckName, cards);
      setImportPreview(preview);
    } catch (error) {
      toast.error(error.message || 'Invalid format');
    } finally {
      setImporting(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!importPreview?.preview_id) return;
    setImporting(true);
    try {
      const result = await storage.confirmImport(importPreview.preview_id);
      toast.success(`Imported: ${result.cards_created} new, ${result.cards_merged} merged`);
      setShowImport(false);
      setImportData('');
      setImportFile(null);
      setImportPreview(null);
      setImportTab('file');
      refetch();
      refetchDecks();
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Export deck
  const handleExport = async () => {
    setShowExport(true);
  };

  const performExport = async (destination = 'device') => {
    setExporting(true);
    setExportingTo(destination);
    try {
      if (destination === 'drive') {
        const result = await backupToGoogleDrive();
        const info = await checkGoogleDriveBackup();
        setGoogleBackupInfo(info);
        toast.success(result.message);
        setShowExport(false);
        return;
      }

      // Get all data from storage
      const allData = await storage.exportAllData();
      const workspaceName = currentWorkspace?.name || 'minddeck';
      const timestamp = new Date().toISOString().split('T')[0];
      
      if (exportFormat === 'json') {
        const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
        await saveOrShareFile(blob, `${workspaceName}-backup-${timestamp}.json`, 'application/json');
      } else if (exportFormat === 'csv') {
        const allCards = [];
        for (const ws of allData.workspaces || []) {
          for (const card of ws.cards || []) {
            allCards.push({
              workspace: ws.name,
              title: card.title,
              notes: card.body || '',
              ...Object.fromEntries(
                Object.entries(card.fields || {}).map(([k, v]) => [k, typeof v === 'object' ? v.value : v])
              )
            });
          }
        }
        
        if (allCards.length === 0) {
          toast.error('No cards to export');
          setExporting(false);
          return;
        }
        
        const allFields = new Set();
        allCards.forEach(c => Object.keys(c).forEach(k => allFields.add(k)));
        const headers = ['workspace', 'title', 'notes', ...Array.from(allFields).filter(h => !['workspace', 'title', 'notes'].includes(h))];
        
        const csvRows = [headers.join(',')];
        for (const card of allCards) {
          const row = headers.map(h => {
            const val = card[h] || '';
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
              return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          });
          csvRows.push(row.join(','));
        }
        
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        await saveOrShareFile(blob, `${workspaceName}-export-${timestamp}.csv`, 'text/csv');
      } else if (exportFormat === 'xlsx') {
        const allCards = [];
        for (const ws of allData.workspaces || []) {
          for (const card of ws.cards || []) {
            allCards.push({
              Workspace: ws.name,
              Title: card.title,
              Notes: card.body || '',
              ...Object.fromEntries(
                Object.entries(card.fields || {}).map(([k, v]) => [k, typeof v === 'object' ? v.value : v])
              )
            });
          }
        }
        
        if (allCards.length === 0) {
          toast.error('No cards to export');
          setExporting(false);
          return;
        }
        
        const worksheet = XLSX.utils.json_to_sheet(allCards);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Cards');
        const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        await saveOrShareFile(blob, `${workspaceName}-export-${timestamp}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      }
      
      toast.success('Export complete');
      setShowExport(false);
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
      setExportingTo(null);
    }
  };

  if (wsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="field-mode">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link to="/" className="text-xl font-bold text-primary">MindDeck</Link>
              <WorkspaceSelector
                onNew={() => setWsShowCreate(true)}
                onRename={openWsRename}
                onDelete={(ws) => setWsToDelete(ws)}
                onDuplicate={handleWsDuplicate}
              />
            </div>

            <div className="flex items-center gap-2">
              {/* Undo Button */}
              {lastDeleted && (
                <Button variant="ghost" size="sm" onClick={handleUndo} className="gap-2" data-testid="undo-btn">
                  <Undo2 className="h-4 w-4" />
                  Undo Delete
                </Button>
              )}

              {/* Import */}
              <Button variant="ghost" size="sm" onClick={() => setShowImport(true)} data-testid="import-btn" className="gap-2">
                <FileUp className="h-4 w-4" />
                Import
              </Button>

              {/* Export */}
              <Button variant="ghost" size="sm" onClick={() => setShowExport(true)} data-testid="export-btn" className="gap-2">
                <FileDown className="h-4 w-4" />
                Export
              </Button>

              {/* Library */}
              <Button variant="ghost" size="sm" onClick={() => navigate('/library')} data-testid="library-btn" className="gap-2">
                <Library className="h-4 w-4" />
                Library
              </Button>

              {/* Dark Mode Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Quick Entry Bar */}
          <div className="mt-3">
            <QuickEntryBar cards={cards} onSuccess={refetch} />
          </div>

          {/* Filter Bar */}
          <div className="flex items-center gap-3 mt-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search cards..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Deck Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Layers className="h-4 w-4" />
                  {currentDeckInfo?.name || 'All Cards'}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setSearchParams({})}>
                  {!currentDeck && <Check className="h-4 w-4 mr-2" />}
                  All Cards
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {decks.map(deck => (
                  <DropdownMenuItem 
                    key={deck.id}
                    onClick={() => setSearchParams({ deck: deck.id })}
                  >
                    {currentDeck === deck.id && <Check className="h-4 w-4 mr-2" />}
                    {deck.name}
                    <Badge variant="outline" className="ml-2">{deck.cards?.length || 0}</Badge>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowDeckCreator(true)}>
                  <FolderPlus className="h-4 w-4 mr-2" /> New Deck
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'az' ? 'za' : 'az')}
              data-testid="sort-btn"
            >
              {sortOrder === 'az' ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
            </Button>

            {/* New Card */}
            <Button onClick={() => { setEditingCard(null); setIsEditorOpen(true); }} data-testid="new-card-btn">
              <Plus className="h-4 w-4 mr-2" /> New Card
            </Button>

            {/* Bulk Delete */}
            {bulkSelectMode ? (
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={toggleBulkSelectMode}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleBulkDeletePreview}
                  disabled={selectedCards.size === 0}
                  data-testid="bulk-delete-confirm-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete ({selectedCards.size})
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                onClick={toggleBulkSelectMode}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Bulk Delete
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Bulk Select Mode Banner */}
      {bulkSelectMode && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 py-2">
          <div className="container mx-auto px-4 text-center text-sm text-amber-800 dark:text-amber-200">
            Click cards to select them for deletion. {selectedCards.size} selected.
          </div>
        </div>
      )}

      {/* Cards Grid */}
      <main className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : displayCards.length === 0 ? (
          <div className="max-w-2xl mx-auto py-10 space-y-6">
            {search ? (
              <p className="text-center text-muted-foreground">No cards match your search.</p>
            ) : (
              <>
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-serif font-medium">Welcome to MindDeck</h2>
                  <p className="text-muted-foreground">Your local knowledge graph. Create a card to get started, or import a deck from the Library.</p>
                </div>

                <div className="bg-muted/40 border border-border rounded-xl p-4 flex gap-3 items-start">
                  <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    MindDeck stores your data locally in this browser. Use <strong>Export</strong> regularly to back up your cards and <strong>Import</strong> to restore them on another device.
                  </p>
                </div>

                <div className="bg-card border border-border rounded-xl p-5 space-y-4">
                  <h3 className="font-medium text-base">How it works</h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">Cards</span>
                      <span>Each card has a title, optional notes, and any number of fields. Fields are key : value pairs — for example <span className="font-mono bg-muted px-1 rounded">Element : Fire</span>.</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">Links</span>
                      <span>Field values automatically become linked cards. Clicking a value navigates to its card, building a knowledge graph as you add more cards.</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">Commas</span>
                      <span>Separate multiple values with commas — <span className="font-mono bg-muted px-1 rounded">Element : Fire, Water</span> — and a dropdown chevron appears on the card to expand and navigate each value individually.</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">Flip ⇄</span>
                      <span>Each field row has a flip button to swap the key and value columns visually — useful when an index card lists many items under one heading.</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">A–Z</span>
                      <span>Sort fields alphabetically by the left column. Toggle between A–Z and Z–A at the top of the fields section on any card.</span>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-primary font-semibold shrink-0">Decks</span>
                      <span>Group related cards into decks for organisation. Import pre-made decks from the Library to get started quickly.</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayCards.map(card => (
              <Card 
                key={card.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow group ${
                  bulkSelectMode && selectedCards.has(card.id) 
                    ? 'ring-2 ring-destructive bg-destructive/5' 
                    : ''
                }`}
                onClick={() => {
                  if (bulkSelectMode) {
                    toggleCardSelection(card.id);
                  } else {
                    navigate(`/card/${card.id}`);
                  }
                }}
                data-testid={`card-${card.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    {bulkSelectMode && (
                      <Checkbox 
                        checked={selectedCards.has(card.id)}
                        onCheckedChange={() => toggleCardSelection(card.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mr-2 mt-1"
                      />
                    )}
                    <CardTitle className="text-lg line-clamp-2 flex-1">{card.title}</CardTitle>
                    {!bulkSelectMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingCard(card); setIsEditorOpen(true); }}>
                            <Edit className="h-4 w-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setAddToDeckCard(card); }}>
                            <FolderPlus className="h-4 w-4 mr-2" /> Add to Deck
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); openDeleteDialog(card); }}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {card.body && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{card.body}</p>
                  )}
                  {Object.keys(card.fields || {}).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(card.fields || {}).slice(0, 3).map(([key, val]) => (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {key}: {typeof val === 'object' ? val.value : val}
                        </Badge>
                      ))}
                      {Object.keys(card.fields).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{Object.keys(card.fields).length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                  {card.decks?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {card.decks.slice(0, 2).map(deckId => {
                        const deck = decks.find(d => d.id === deckId);
                        return deck ? (
                          <Badge key={deckId} variant="outline" className="text-xs">
                            {deck.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-card py-6 mt-8">
        <div className="container mx-auto px-4 text-center space-y-3">
          <p className="text-sm font-medium">
            MindDeck - Your programmable knowledge base
          </p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Create, organize, and manage flashcards and knowledge cards locally on your device. 
            Optional Google Drive backup lets you sync your data across devices securely.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm">
            <Link 
              to="/privacy"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>

      {/* Card Editor */}
      <CardEditor
        isOpen={isEditorOpen}
        onClose={() => { setIsEditorOpen(false); setEditingCard(null); }}
        card={editingCard}
        onSave={() => { refetch(); toast.success(editingCard ? 'Card updated' : 'Card created'); }}
        workspaceId={currentWorkspace?.id}
      />

      {/* Deck Creator */}
      <DeckCreator
        isOpen={showDeckCreator}
        onClose={() => setShowDeckCreator(false)}
        onSave={refetchDecks}
        workspaceId={currentWorkspace?.id}
      />

      {/* Add to Deck Dialog */}
      <AddToDeckDialog
        isOpen={!!addToDeckCard}
        onClose={() => setAddToDeckCard(null)}
        card={addToDeckCard}
        workspaceId={currentWorkspace?.id}
        onSuccess={refetch}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteCard} onOpenChange={() => { setDeleteCard(null); setDeletionPreview(null); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Card
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>Are you sure you want to delete <strong>"{deleteCard?.title}"</strong>?</p>
                
                {loadingPreview && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-sm">Checking for related fields...</span>
                  </div>
                )}
                
                {deletionPreview?.affected_cards?.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-2">
                      This will also remove {deletionPreview.total_fields_removed} field(s) from {deletionPreview.affected_cards.length} other card(s):
                    </p>
                    <ScrollArea className="max-h-48">
                      {deletionPreview.affected_cards.map((affected) => (
                        <div key={affected.card_id} className="bg-background/50 rounded p-2 text-sm mb-2">
                          <div className="font-medium">{affected.card_title}</div>
                          <ul className="mt-1">
                            {affected.fields_to_remove.map((field, idx) => (
                              <li key={idx} className="text-muted-foreground text-xs flex items-center gap-1">
                                <span className="text-red-500">✕</span>
                                <span className="font-mono">{field.field_name}: {field.field_value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                )}
                
                {deletionPreview && !deletionPreview.affected_cards?.length && (
                  <p className="text-sm text-muted-foreground">✓ No other cards reference this card.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={isDeleting || loadingPreview}
            >
              {isDeleting ? 'Deleting...' : deletionPreview?.total_fields_removed > 0 
                ? `Delete Card & ${deletionPreview.total_fields_removed} Field(s)`
                : 'Delete Card'
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={!!bulkDeletePreview} onOpenChange={() => setBulkDeletePreview(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete {bulkDeletePreview?.totalSelected} Cards?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>The following cards will be deleted:</p>
                
                {/* Selected cards */}
                <div>
                  <h4 className="font-medium text-sm mb-2">Selected for deletion ({bulkDeletePreview?.selected?.length})</h4>
                  <ScrollArea className="h-32 border rounded-md p-2">
                    <ul className="space-y-1">
                      {bulkDeletePreview?.selected?.map(card => (
                        <li key={card.id} className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-destructive" />
                          {card.title}
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </div>
                
                {/* Affected cards */}
                {bulkDeletePreview?.affected?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2 text-amber-600 dark:text-amber-400">
                      Also affected ({bulkDeletePreview?.affected?.length} cards)
                    </h4>
                    <ScrollArea className="h-32 border border-amber-200 dark:border-amber-800 rounded-md p-2 bg-amber-50/50 dark:bg-amber-950/20">
                      <ul className="space-y-1">
                        {bulkDeletePreview?.affected?.map(card => (
                          <li key={card.card_id} className="text-sm text-amber-800 dark:text-amber-200">
                            {card.card_title} - will have fields removed
                          </li>
                        ))}
                      </ul>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : `Delete ${bulkDeletePreview?.totalSelected} Cards`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={(open) => {
        setShowImport(open);
        if (!open) {
          setImportData('');
          setImportFile(null);
          setImportPreview(null);
          setImportTab('file');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Cards</DialogTitle>
            <DialogDescription>
              Upload a file or paste JSON to import cards into a new deck.
            </DialogDescription>
          </DialogHeader>
          
          {!importPreview ? (
            <Tabs value={importTab} onValueChange={setImportTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="file">Upload File</TabsTrigger>
                <TabsTrigger value="text">Paste JSON</TabsTrigger>
                <TabsTrigger value="drive">Google Drive</TabsTrigger>
              </TabsList>
              
              <TabsContent value="file" className="space-y-4 mt-4">
                <div 
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => importFileRef.current?.click()}
                >
                  <input
                    ref={importFileRef}
                    type="file"
                    accept=".json,.csv,.xls,.xlsx"
                    onChange={handleImportFileSelect}
                    className="hidden"
                  />
                  {importFile ? (
                    <div className="flex flex-col items-center gap-2">
                      {getImportFileIcon(importFile.name)}
                      <p className="font-medium">{importFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-10 w-10 text-muted-foreground" />
                      <p className="text-muted-foreground">Click to select file</p>
                      <p className="text-xs text-muted-foreground">.json, .csv, .xls, .xlsx</p>
                    </div>
                  )}
                </div>
                
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>.json</strong> - Cards with deck_name and cards array</p>
                  <p><strong>.csv / .xls / .xlsx</strong> - Columns: title, body/notes, + field columns</p>
                </div>
                
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => navigate('/import-help')}>
                    View Format Help
                  </Button>
                  <Button onClick={handleImportPreview} disabled={!importFile || importing}>
                    {importing ? 'Processing...' : 'Preview Import'}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="text" className="space-y-4 mt-4">
                <Textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder='{"deck_name": "My Import", "cards": [{"title": "Card 1", "fields": {"Field": "Value"}}]}'
                  rows={10}
                  className="font-mono text-sm"
                />
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => navigate('/import-help')}>
                    View Format Help
                  </Button>
                  <Button onClick={handleImportPreview} disabled={!importData.trim() || importing}>
                    {importing ? 'Processing...' : 'Preview Import'}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="drive" className="space-y-4 mt-4">
                {!googleReady ? (
                  <p className="text-sm text-muted-foreground">Google Drive is not configured.</p>
                ) : !googleConnected ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Connect Google Drive to restore from a previous backup.
                    </p>
                    <Button variant="outline" onClick={() => startGoogleRedirectAuth()}>
                      <Cloud className="h-4 w-4 mr-2 text-blue-500" />
                      Connect Google Drive
                    </Button>
                  </div>
                ) : !googleBackupInfo?.exists ? (
                  <p className="text-sm text-muted-foreground">
                    No backup found in your Google Drive. Export one first using the Export button above.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Backup saved: {googleBackupInfo?.lastModified ? new Date(googleBackupInfo.lastModified).toLocaleString() : 'Unknown'}
                    </p>
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Warning:</strong> This will replace all current data on this device. This cannot be undone.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      disabled={googleLoading}
                      onClick={async () => {
                        try {
                          setGoogleLoading(true);
                          const result = await restoreFromGoogleDrive();
                          toast.success(result.message);
                          setShowImport(false);
                          window.location.reload();
                        } catch (error) {
                          toast.error(error.message || 'Restore failed');
                        } finally {
                          setGoogleLoading(false);
                        }
                      }}
                    >
                      {googleLoading
                        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        : <CloudDownload className="h-4 w-4 mr-2" />}
                      Restore from Google Drive
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="space-y-4 py-4">
              <div className="flex gap-4 text-sm">
                <div className="bg-green-100 dark:bg-green-900/30 rounded px-3 py-2">
                  <span className="font-medium text-green-800 dark:text-green-200">
                    {importPreview.cards_to_create} new cards
                  </span>
                </div>
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded px-3 py-2">
                  <span className="font-medium text-blue-800 dark:text-blue-200">
                    {importPreview.cards_to_merge} cards to merge
                  </span>
                </div>
              </div>
              
              <ScrollArea className="h-64 border rounded-md p-2">
                {importPreview.cards.map((card, idx) => (
                  <div key={idx} className="flex items-center gap-2 py-2 border-b last:border-0">
                    <Badge variant={card.action === 'create' ? 'default' : 'secondary'}>
                      {card.action === 'create' ? 'NEW' : 'MERGE'}
                    </Badge>
                    <span className="flex-1 font-medium">{card.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {card.incoming_field_count} fields
                    </span>
                  </div>
                ))}
              </ScrollArea>
              
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setImportPreview(null)}>
                  Back
                </Button>
                <Button onClick={handleImportConfirm} disabled={importing}>
                  {importing ? 'Importing...' : 'Confirm Import'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Data</DialogTitle>
            <DialogDescription>
              Download all your workspaces, cards, and decks
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label>Choose format:</Label>
              
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${exportFormat === 'xlsx' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                onClick={() => setExportFormat('xlsx')}
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-6 w-6 text-green-600" />
                  <div>
                    <div className="font-medium">Spreadsheet (.xlsx)</div>
                    <div className="text-sm text-muted-foreground">Excel format - easy to view and edit</div>
                  </div>
                </div>
              </div>
              
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${exportFormat === 'csv' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                onClick={() => setExportFormat('csv')}
              >
                <div className="flex items-center gap-3">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <div>
                    <div className="font-medium">CSV (.csv)</div>
                    <div className="text-sm text-muted-foreground">Universal spreadsheet format</div>
                  </div>
                </div>
              </div>
              
              <div 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${exportFormat === 'json' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                onClick={() => setExportFormat('json')}
              >
                <div className="flex items-center gap-3">
                  <FileDown className="h-6 w-6 text-purple-600" />
                  <div>
                    <div className="font-medium">JSON (.json)</div>
                    <div className="text-sm text-muted-foreground">Full backup - best for restore</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowExport(false)} className="sm:mr-auto">
              Cancel
            </Button>
            <Button onClick={() => performExport('device')} disabled={exporting} variant="outline">
              {exporting && exportingTo === 'device'
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <FileDown className="h-4 w-4 mr-1" />}
              Save to Device
            </Button>
            {googleReady && !googleConnected && (
              <Button variant="outline" onClick={() => startGoogleRedirectAuth()} disabled={exporting}>
                <Cloud className="h-4 w-4 mr-1 text-blue-500" />
                Connect Google Drive
              </Button>
            )}
            {googleReady && googleConnected && (
              <Button onClick={() => performExport('drive')} disabled={exporting} variant="outline">
                {exporting && exportingTo === 'drive'
                  ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  : <CloudUpload className="h-4 w-4 mr-1 text-blue-500" />}
                Save to Google Drive
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Workspace Modals ─────────────────────────────────────────────────
           Native <dialog> elements render in the browser top layer — no z-index
           or stacking context conflicts with Radix or sticky headers.
      ──────────────────────────────────────────────────────────────────────── */}

      <WsModal open={wsShowCreate} onClose={() => { setWsShowCreate(false); setWsNewName(''); }}>
        <h2 className="text-lg font-semibold mb-4">Create Workspace</h2>
        <div className="mb-4">
          <Label className="mb-1 block">Name</Label>
          <Input
            value={wsNewName}
            onChange={(e) => setWsNewName(e.target.value)}
            placeholder="My Workspace"
            onKeyDown={(e) => e.key === 'Enter' && handleWsCreate()}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setWsShowCreate(false); setWsNewName(''); }}>
            Cancel
          </Button>
          <Button onClick={handleWsCreate} disabled={wsCreating || !wsNewName.trim()}>
            {wsCreating ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </WsModal>

      <WsModal open={!!wsToDelete} onClose={() => setWsToDelete(null)}>
        <h2 className="text-lg font-semibold mb-2">Delete Workspace?</h2>
        <p className="text-sm text-muted-foreground mb-6">
          This will permanently delete <strong>"{wsToDelete?.name}"</strong> and all its cards and decks. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setWsToDelete(null)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleWsDelete}>
            Delete
          </Button>
        </div>
      </WsModal>

      <WsModal open={!!wsToRename} onClose={() => setWsToRename(null)}>
        <h2 className="text-lg font-semibold mb-4">Rename Workspace</h2>
        <div className="mb-4">
          <Label className="mb-1 block">New name</Label>
          <Input
            value={wsRenameValue}
            onChange={(e) => setWsRenameValue(e.target.value)}
            placeholder="Workspace name"
            onKeyDown={(e) => e.key === 'Enter' && handleWsRename()}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setWsToRename(null)}>
            Cancel
          </Button>
          <Button onClick={handleWsRename} disabled={!wsRenameValue.trim()}>
            Rename
          </Button>
        </div>
      </WsModal>

    </div>
  );
}

export default FieldMode;
